import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  apiService as governanceApi,
  consumeActiveTenantWarmPayload,
  type ActiveTenantPayload,
  type GovernanceAuthzTenant,
} from "@/lib/api/governance-api";
import { clearSharedReadCache, clearTenantScopedReadCache } from "@/lib/api/client-shared";
import { publishActiveTenantQueryScope } from "@/lib/hooks/query-options";
import { getPlatformQueryClient } from "@/lib/providers/query-provider";
import { clearFrontendConfigCache } from "@/lib/hooks/use-frontend-config";
import { useI18nContext } from "@/lib/i18n/context";
import { createSharedContext } from "@/lib/utils";
import { apiFailureMessage } from "../lib/utils/response";
import { useSecurityContext } from "./SecurityContext";

interface TenantContextValue {
  activeTenant: GovernanceAuthzTenant | null;
  activeTenantId: string;
  activeTenantSource: string;
  tenants: GovernanceAuthzTenant[];
  canSwitch: boolean;
  isLoading: boolean;
  isSwitching: boolean;
  error: string;
  refreshActiveTenant: () => Promise<void>;
  switchActiveTenant: (tenantId: string) => Promise<boolean>;
}

const TenantContext = createSharedContext<TenantContextValue | null>("__9s_tenant_react_context__", null);

function tenantIdFromPayload(payload: ActiveTenantPayload | null): string {
  if (!payload) {
    return "";
  }
  const activeTenantId = String(payload.active_tenant_id ?? "").trim();
  if (activeTenantId) {
    return activeTenantId;
  }
  return String(payload.active_tenant?.tenant_id ?? "").trim();
}

function tenantListFromPayload(payload: ActiveTenantPayload | null): GovernanceAuthzTenant[] {
  if (!Array.isArray(payload?.tenants)) {
    return [];
  }
  return payload.tenants.filter((tenant): tenant is GovernanceAuthzTenant => {
    if (!tenant) {
      return false;
    }
    return typeof tenant.tenant_id === "string" && tenant.tenant_id.trim().length > 0;
  });
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { t } = useI18nContext();
  const { state: securityState, refreshPolicySnapshot } = useSecurityContext();
  const [activeTenant, setActiveTenant] = useState<GovernanceAuthzTenant | null>(null);
  const [activeTenantId, setActiveTenantId] = useState("");
  const [activeTenantSource, setActiveTenantSource] = useState("");
  const [tenants, setTenants] = useState<GovernanceAuthzTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState("");
  const refreshInflightRef = useRef<Promise<void> | null>(null);
  const switchGenerationRef = useRef(0);

  const applyPayload = useCallback((payload: ActiveTenantPayload | null) => {
    const storedId = tenantIdFromPayload(payload);
    publishActiveTenantQueryScope(storedId);
    const resolvedRaw = payload?.resolved_active_tenant_id;
    const resolvedId = typeof resolvedRaw === "string" ? resolvedRaw.trim() : "";
    setActiveTenant(payload?.active_tenant ?? null);
    setActiveTenantId(storedId);
    setActiveTenantSource(String(payload?.active_tenant_source ?? "").trim());
    setTenants(tenantListFromPayload(payload));
    if (resolvedId && resolvedId !== storedId) {
      console.warn(
        `[tenant] resolved_active_tenant_id=${resolvedId} differs from active_tenant_id=${storedId}`,
      );
      setError(t("header.tenant.resolveMismatch", { stored: storedId, resolved: resolvedId }));
    }
  }, [t]);

  const refreshActiveTenant = useCallback(async () => {
    if (!securityState.isAuthenticated) {
      applyPayload(null);
      setIsLoading(false);
      return;
    }
    if (refreshInflightRef.current) {
      await refreshInflightRef.current;
      return;
    }
    const job = (async () => {
      const generation = switchGenerationRef.current;
      setIsLoading(true);
      setError("");
      const response = await governanceApi.getActiveTenant({ view: "shell", swr: false, force: true });
      if (generation !== switchGenerationRef.current) {
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      if (!response.success) {
        setError(apiFailureMessage(response));
        return;
      }
      let payload = response.data ?? null;
      const shellTenant = payload?.active_tenant ?? null;
      const shellTaxId = String(shellTenant?.metadata?.legal?.tax_id ?? "").trim();
      if (shellTenant !== null && shellTaxId.length === 0) {
        const fullResponse = await governanceApi.getActiveTenant({ swr: false, force: true });
        if (generation !== switchGenerationRef.current) {
          setIsLoading(false);
          return;
        }
        const fullTenant = fullResponse.success ? fullResponse.data?.active_tenant ?? null : null;
        const fullLegal = fullTenant?.metadata?.legal;
        if (fullLegal !== undefined && payload !== null) {
          payload = {
            ...payload,
            active_tenant: {
              ...shellTenant,
              metadata: {
                ...(shellTenant.metadata ?? {}),
                legal: fullLegal,
              },
            },
          };
        }
      }
      if (generation !== switchGenerationRef.current) {
        setIsLoading(false);
        return;
      }
      applyPayload(payload);
    })();
    refreshInflightRef.current = job;
    try {
      await job;
    } finally {
      refreshInflightRef.current = null;
    }
  }, [applyPayload, securityState.isAuthenticated, t]);

  const switchActiveTenant = useCallback(
    async (tenantId: string) => {
      const nextTenantId = String(tenantId).trim();
      if (!nextTenantId) {
        return false;
      }
      switchGenerationRef.current += 1;
      const generation = switchGenerationRef.current;
      clearSharedReadCache("authz");
      setIsSwitching(true);
      setError("");
      const response = await governanceApi.putActiveTenant(nextTenantId);
      if (generation !== switchGenerationRef.current) {
        return false;
      }
      setIsSwitching(false);
      if (!response.success) {
        setError(apiFailureMessage(response));
        return false;
      }
      const payload = response.data !== undefined && response.data !== null ? response.data : null;
      applyPayload(payload);
      clearTenantScopedReadCache();
      clearFrontendConfigCache();
      sessionStorage.removeItem("ih_records_list_query");
      sessionStorage.removeItem("ih_ledger_list_query");
      sessionStorage.removeItem("ih_drafts_list_query");
      sessionStorage.removeItem("intake_drafts_list_query");
      const queryClient = getPlatformQueryClient();
      await queryClient.cancelQueries();
      queryClient.removeQueries();
      await refreshPolicySnapshot({ force: true });
      return true;
    },
    [applyPayload, refreshPolicySnapshot, t],
  );

  useEffect(() => {
    if (!securityState.sessionHydrated) {
      return;
    }
    if (!securityState.isAuthenticated) {
      applyPayload(null);
      setIsLoading(false);
      return;
    }
    const warmPayload = consumeActiveTenantWarmPayload();
    if (warmPayload) {
      applyPayload(warmPayload);
    }
    void refreshActiveTenant();
  }, [applyPayload, refreshActiveTenant, securityState.isAuthenticated, securityState.sessionHydrated]);

  const value = useMemo<TenantContextValue>(
    () => ({
      activeTenant,
      activeTenantId,
      activeTenantSource,
      tenants,
      canSwitch: tenants.some((tenant) => tenant.tenant_id !== activeTenantId && tenant.switchable === true),
      isLoading,
      isSwitching,
      error,
      refreshActiveTenant,
      switchActiveTenant,
    }),
    [
      activeTenant,
      activeTenantId,
      activeTenantSource,
      tenants,
      isLoading,
      isSwitching,
      error,
      refreshActiveTenant,
      switchActiveTenant,
    ],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenantContext(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenantContext must be used within TenantProvider");
  }
  return context;
}
